import { Flex, Input, type FlexProps, type InputProps } from "@chakra-ui/react";
import { forwardRef, type ReactNode } from "react";

export type FormInputProps = Omit<InputProps, "size" | "visual"> & {
  /** Shown inside the field before the text, e.g. a "$" or a search icon. */
  startElement?: ReactNode;
  /**
   * Overrides for the bordered field around the input, for a field shaped to
   * sit in a row of other controls, such as the admin events page's pill
   * search box.
   */
  wrapperProps?: FlexProps;
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  ({ startElement, wrapperProps, ...props }, ref) => (
    <Flex
      align="center"
      w="100%"
      h="52px"
      px="14px"
      bg="bg.canvas"
      borderRadius="md"
      borderWidth="1.5px"
      borderColor="border.default"
      transition="border-color 120ms, box-shadow 120ms"
      _focusWithin={{
        borderColor: "border.brand",
        boxShadow: "0 0 0 4px {colors.bg.brandTint}",
      }}
      gap="2"
      {...wrapperProps}
    >
      {startElement && (
        <Flex color="fg.muted" flexShrink={0} align="center">
          {startElement}
        </Flex>
      )}
      <Input
        ref={ref}
        flex="1"
        minW="0"
        border="0"
        outline="0"
        bg="transparent"
        p="0"
        // The whole height of the field, not one line of text: an empty
        // input draws its caret at the font's full height, which is taller
        // than a line, and a one-line box cut the caret off at the top and
        // bottom until something was typed. The text stays centered.
        h="full"
        fontFamily="body"
        // 16px on mobile prevents iOS Safari from auto-zooming the page on
        // focus (it zooms any focused input under 16px); 15px from `md` up
        // keeps the desktop design. Set on the component so it shares Chakra's
        // style-prop cascade layer — a globalCss rule loses to this layer
        // regardless of selector specificity.
        fontSize={{ base: "16px", md: "15px" }}
        fontWeight="medium"
        color="fg.default"
        letterSpacing="-0.005em"
        _focus={{ boxShadow: "none", borderColor: "transparent" }}
        _placeholder={{ color: "fg.muted" }}
        {...props}
      />
    </Flex>
  ),
);
FormInput.displayName = "FormInput";
